export interface ParsedLegalSectionCitation {
  sectionId: string
  label: string
  markdown: string
}

export function normalizeLegalSectionId(value: unknown): string {
  return String(value || '').trim().toLowerCase()
}

/** Spanish connectors kept lowercase inside a title (except as the first word). */
const TITLE_LOWER_WORDS = new Set([
  'de', 'del', 'la', 'las', 'el', 'los', 'y', 'e', 'o', 'u', 'a', 'en',
  'para', 'por', 'con', 'al', 'sobre',
])

/**
 * Clean up a norm title for display. Corpus norm titles are inconsistently cased — many are stored
 * SHOUTING (`CÓDIGO CIVIL`). When a multi-word title is all-caps we reflow it to title case
 * (`Código Civil`), keeping Spanish connectors lowercase; already-cased titles are returned verbatim,
 * and single-token all-caps strings are left alone (likely acronyms: `LEPINA`, `LACAP`).
 */
export function prettifyNormTitle(title: string): string {
  const t = String(title || '').trim()
  if (!t) return t
  const words = t.split(/\s+/)
  if (words.length < 2) return t // acronym or single word — don't touch
  const letters = t.replace(/[^\p{L}]/gu, '')
  if (!letters || letters !== letters.toLocaleUpperCase()) return t // not all-caps — leave as authored
  return words
    .map((word, i) => {
      const lower = word.toLocaleLowerCase()
      if (i > 0 && TITLE_LOWER_WORDS.has(lower)) return lower
      return lower.replace(/\p{L}/u, c => c.toLocaleUpperCase())
    })
    .join(' ')
}

/**
 * THE canonical human-readable citation label: `Art. 624 (Código Civil)`. Replaces the legacy
 * em-dash join (`Art. 624 — CÓDIGO CIVIL`). This is built into the model-facing tool output (so the
 * label the model copies into its answer is already clean) AND reused by the UI popover, so every
 * surface renders the same way.
 */
export function formatCitationLabel(displayRef: string | null | undefined, normTitle: string): string {
  const norm = prettifyNormTitle(normTitle)
  const ref = String(displayRef || '').trim()
  return ref ? `${ref} (${norm})` : norm
}

/**
 * Close a citation href the model forgot to close before comma-joining the next one.
 *
 * Models comma-join citations that belong to the same claim, and when they do they drop the closing
 * paren of the first link:
 *
 *   [@Art. 58 (Código de Trabajo)](cite:section/<id1>, [@Art. 5 (…)](cite:section/<id2>)
 *
 * Every marker parser here requires a `)` immediately after the uuid, so the first citation matches
 * nothing and its RAW MARKDOWN — brackets, `cite:section/`, a bare uuid — is printed to the user
 * inside a legal answer. Observed on 2026-08-24 in a despido-injustificado answer.
 *
 * The repair is exact, not a guess: the text is already balanced (the trailing `)` closes the LAST
 * href), so the only thing missing is the one paren before the comma. Inserting it makes both links
 * parse and changes nothing else — a `cite:section/<uuid>` followed by a comma has no other reading.
 *
 * The prompt should still ask for one link per citation. This is the floor under it, because a
 * malformed marker must degrade to a plain citation, never to visible markup.
 */
export function repairLegalCitationMarkers(text: string): string {
  return String(text || '').replace(/(\]\(cite:section\/[a-f0-9-]{36})\s*,/gi, '$1),')
}

/**
 * Drop an article slot that is really a section UUID.
 *
 * A citation label is authored by the MODEL, copied from what a tool handed it. When a tool gave it
 * an id but no finished label, it has been observed to fill the `Art. N` slot with the id — so a
 * chip reads `Art. 9f41c5d1-772c-4a1f-a99c-27cf5a336b9a (Código de Trabajo)` in an answer whose own
 * prose calls the same provision Artículo 198. A 36-character hex string is never an article number,
 * so this is a safe, exact rule rather than a guess.
 *
 * STILL LOAD-BEARING, though quick-research no longer needs it: that agent stopped writing citations
 * altogether (it emits `<section_id>` and its server renders the link — MAR-462), because handing a
 * model a finished label to copy did not work. Every other producer still has a model handling the
 * two-part construct — `server/api/public/research.post.ts` composes markers, and the chat coordinator
 * relays quick-research's byte-for-byte — so this remains the floor under all of them: the norm
 * title alone is a true, readable citation; a UUID is neither.
 */
export function sanitizeCitationLabel(label: string): string {
  const cleaned = String(label || '')
    .replace(/\b(?:art\.?|arts\.?|artículo|articulo|article|section|§)\s*[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\s*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .replace(/^[—–\-,;:]\s*/, '')
  if (!cleaned) return String(label || '')
  // `Art. <uuid> (Código de Trabajo)` collapses to `(Código de Trabajo)`; the parens are now noise.
  const unwrapped = cleaned.replace(/^\((.+)\)$/, '$1').trim()
  return unwrapped || cleaned
}

export function matchLegalSectionCitationAtStart(src: string): ParsedLegalSectionCitation | null {
  const match = String(src || '').match(/^(\[@?([^\]]+)\]\(cite:section\/([a-f0-9-]{36})\))/i)
  if (!match) return null

  const sectionId = normalizeLegalSectionId(match[3])
  if (!sectionId) return null

  return {
    markdown: match[1] || '',
    label: sanitizeCitationLabel(match[2] || ''),
    sectionId,
  }
}

export function parseLegalSectionCitations(text: string): ParsedLegalSectionCitation[] {
  const citeRegex = /\[@?([^\]]+)\]\(cite:section\/([a-f0-9-]{36})\)/gi
  const seen = new Set<string>()
  const citations: ParsedLegalSectionCitation[] = []

  for (const match of repairLegalCitationMarkers(text).matchAll(citeRegex)) {
    const markdown = match[0] || ''
    const label = sanitizeCitationLabel(match[1] || '')
    const sectionId = normalizeLegalSectionId(match[2])
    if (!sectionId || seen.has(sectionId)) continue
    seen.add(sectionId)
    citations.push({ sectionId, label, markdown })
  }

  return citations
}

export function parseLegalCitationTargets(text: string): string[] {
  return [...String(text || '').matchAll(/\[[^\]]+\]\(cite:([^)]+)\)/gi)]
    .map(match => String(match[1] || '').trim())
    .filter(Boolean)
}
